import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "../ModalShell";

export const NO_CHANGE = "__no_change__";
const SET_VALUE = "__set_value__";

/**
 * Reusable bulk update modal.
 *
 * Field definitions:
 * - { id, label, type: "select", options: [{ value, label }], allowEmpty?: boolean }
 * - { id, label, type: "text", placeholder?, helpText? }
 * - { id, label, type: "date", helpText? }
 *
 * Behavior:
 * - All fields default to "No change"
 * - Text fields require choosing "Set value" before input is enabled
 * - Select fields always include a "No change" option first (caller options are appended)
 */
export default function BulkUpdateFieldsModal({
  open,
  onClose,
  title,
  subtitle,
  selectedCount = 0,
  fields = [],
  loading = false,
  onApply,
  applyLabel = "Apply update",
}) {
  const normalizedFields = useMemo(() => (Array.isArray(fields) ? fields.filter(Boolean) : []), [fields]);

  const [selectValues, setSelectValues] = useState({});
  const [textModes, setTextModes] = useState({});
  const [textValues, setTextValues] = useState({});
  const [dateModes, setDateModes] = useState({});
  const [dateValues, setDateValues] = useState({});

  useEffect(() => {
    if (!open) return;
    const nextSelect = {};
    const nextModes = {};
    const nextText = {};
    const nextDateModes = {};
    const nextDateValues = {};
    normalizedFields.forEach((f) => {
      if (f.type === "select") nextSelect[f.id] = NO_CHANGE;
      if (f.type === "text") {
        nextModes[f.id] = NO_CHANGE;
        nextText[f.id] = "";
      }
      if (f.type === "date") {
        nextDateModes[f.id] = NO_CHANGE;
        nextDateValues[f.id] = "";
      }
    });
    setSelectValues(nextSelect);
    setTextModes(nextModes);
    setTextValues(nextText);
    setDateModes(nextDateModes);
    setDateValues(nextDateValues);
  }, [open, normalizedFields]);

  const canApply = useMemo(() => {
    if (!selectedCount || selectedCount <= 0) return false;
    let changed = false;
    for (const f of normalizedFields) {
      if (f.type === "select") {
        const v = selectValues[f.id];
        if (v != null && v !== NO_CHANGE) {
          changed = true;
          const allowEmpty = !!f.allowEmpty;
          if (!allowEmpty && String(v) === "") return false;
        }
      } else if (f.type === "text") {
        const mode = textModes[f.id];
        if (mode != null && mode !== NO_CHANGE) {
          changed = true;
        }
      } else if (f.type === "date") {
        const mode = dateModes[f.id];
        if (mode != null && mode !== NO_CHANGE) {
          changed = true;
          if (!String(dateValues[f.id] || "").trim()) return false;
        }
      }
    }
    return changed;
  }, [normalizedFields, selectValues, selectedCount, textModes, dateModes, dateValues]);

  return (
    <ModalShell
      open={open}
      onClose={loading ? undefined : onClose}
      title={title || `Bulk update (${selectedCount || 0})`}
      subtitle={subtitle || "Choose values to apply to all selected records."}
      maxWidth={640}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <button type="button" className="btn btn-md btn-muted" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary"
            disabled={!canApply || loading}
            title={!canApply ? "Pick at least one field to update" : undefined}
            onClick={() => {
              if (!onApply) return;
              const payload = {};
              normalizedFields.forEach((f) => {
                if (f.type === "select") {
                  const v = selectValues[f.id];
                  if (v != null && v !== NO_CHANGE) payload[f.id] = v;
                } else if (f.type === "text") {
                  const mode = textModes[f.id];
                  if (mode != null && mode !== NO_CHANGE) payload[f.id] = String(textValues[f.id] || "").trim();
                } else if (f.type === "date") {
                  const mode = dateModes[f.id];
                  if (mode != null && mode !== NO_CHANGE) payload[f.id] = String(dateValues[f.id] || "").trim();
                }
              });
              onApply(payload);
            }}
          >
            {loading ? "Updating..." : applyLabel}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {normalizedFields.map((f) => {
          if (!f || !f.id) return null;
          if (f.type === "select") {
            const options = Array.isArray(f.options) ? f.options.filter(Boolean) : [];
            const value = selectValues[f.id] ?? NO_CHANGE;
            return (
              <div key={f.id} style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, alignItems: "center" }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: "#212529" }}>{f.label || f.id}</label>
                <select
                  value={value}
                  onChange={(e) => setSelectValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                  disabled={loading}
                  className="input"
                  style={{ minWidth: 320 }}
                >
                  <option value={NO_CHANGE}>No change</option>
                  {options.map((opt) => (
                    <option key={`${f.id}:${String(opt.value)}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }
          if (f.type === "text") {
            const mode = textModes[f.id] ?? NO_CHANGE;
            const value = textValues[f.id] ?? "";
            return (
              <div key={f.id} style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, alignItems: "center" }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: "#212529" }}>{f.label || f.id}</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr",
                    gap: 10,
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <select
                    value={mode}
                    onChange={(e) => setTextModes((prev) => ({ ...prev, [f.id]: e.target.value }))}
                    disabled={loading}
                    className="input"
                    style={{ minWidth: 160 }}
                  >
                    <option value={NO_CHANGE}>No change</option>
                    <option value={SET_VALUE}>Set value</option>
                  </select>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setTextValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                    disabled={loading || mode === NO_CHANGE}
                    className="input"
                    placeholder={f.placeholder || "Enter value (blank to clear)"}
                    style={{ width: "100%", minWidth: 0 }}
                  />
                </div>
                {f.helpText ? (
                  <div style={{ gridColumn: "2 / -1", fontSize: 12, color: "#6c757d", marginTop: -6 }}>
                    {f.helpText}
                  </div>
                ) : null}
              </div>
            );
          }
          if (f.type === "date") {
            const mode = dateModes[f.id] ?? NO_CHANGE;
            const value = dateValues[f.id] ?? "";
            return (
              <div key={f.id} style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, alignItems: "center" }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: "#212529" }}>{f.label || f.id}</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr",
                    gap: 10,
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <select
                    value={mode}
                    onChange={(e) => setDateModes((prev) => ({ ...prev, [f.id]: e.target.value }))}
                    disabled={loading}
                    className="input"
                    style={{ minWidth: 160 }}
                  >
                    <option value={NO_CHANGE}>No change</option>
                    <option value={SET_VALUE}>Set value</option>
                  </select>
                  <input
                    type="date"
                    value={value}
                    onChange={(e) => setDateValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                    disabled={loading || mode === NO_CHANGE}
                    className="input"
                    style={{ width: "100%", minWidth: 0 }}
                  />
                </div>
                {f.helpText ? (
                  <div style={{ gridColumn: "2 / -1", fontSize: 12, color: "#6c757d", marginTop: -6 }}>
                    {f.helpText}
                  </div>
                ) : null}
              </div>
            );
          }
          return null;
        })}
        <div style={{ fontSize: 12, color: "#6c757d" }}>Tip: fields stay unchanged unless you pick a value.</div>
      </div>
    </ModalShell>
  );
}

